import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Button, Card, Pill } from "@/components/ui";
import { useAuth, useToast } from "@/contexts";
import { getPublicInvitePreview, joinCompetition } from "@/features/competitions/api";
import { getProviderConnections, hasConnectedProvider } from "@/features/sync/connections";
import type { PublicInvitePreview } from "@/types";

export default function JoinCode() {
  const { code } = useParams({ from: "/join/$code" }) as { code: string };
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invite, setInvite] = useState<PublicInvitePreview | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!code) return;
    (async () => {
      const { data, error } = await getPublicInvitePreview(code);
      if (error || !data) {
        setNotFound(true);
      } else {
        setInvite(data);
      }
      setLoading(false);
    })();
  }, [code]);

  async function handleJoin() {
    if (!code || joining || !invite) return;
    setJoining(true);

    const connections = await getProviderConnections();
    if (!hasConnectedProvider(connections)) {
      navigate({
        to: "/health-setup",
        search: { redirect: `/join/${code}` },
        replace: true,
      });
      return;
    }

    const { error } = await joinCompetition(code);
    if (error) {
      toast.showAlert("Join failed", error.message, "error");
      setJoining(false);
    } else {
      toast.showAlert("Joined!", `You're now in ${invite.competitionName}`, "success");
      navigate({ to: `/competition/${invite.competitionId}`, replace: true });
    }
  }

  if (loading) {
    return (
      <Screen title="Joining..." eyebrow="Join">
        <div className="animate-pulse bg-card rounded-[20px] p-4 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)]">
          <div className="h-4 bg-[rgba(28,27,46,0.08)] rounded w-3/4" />
        </div>
      </Screen>
    );
  }

  if (notFound || !invite) {
    return (
      <Screen title="Invalid code" eyebrow="Join">
        <Card>
          <Pill label="Error" tone="warning" />
          <p className="text-text-muted text-sm leading-5">
            No competition found with that invite code. Check the code and try again.
          </p>
          <Button label="Back to join" onPress={() => navigate({ to: "/join" })} />
        </Card>
      </Screen>
    );
  }

  return (
    <Screen title={invite.competitionName} eyebrow="Join">
      <Card>
        <Pill label="Invite-only" tone="success" />
        <p className="text-text-muted text-sm leading-5">
          {invite.inviterName} challenged you to a step competition with {invite.memberCount}{" "}
          participant{invite.memberCount === 1 ? "" : "s"} so far.
        </p>
        <div className="flex flex-col gap-1">
          <span className="text-text-dim text-xs font-bold uppercase tracking-wide">Starts</span>
          <span className="text-text text-sm">{invite.startsAt}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-text-dim text-xs font-bold uppercase tracking-wide">Ends</span>
          <span className="text-text text-sm">{invite.endsAt}</span>
        </div>
        {user ? (
          <Button label={joining ? "Joining..." : "Join competition"} onPress={handleJoin} />
        ) : (
          <div className="flex flex-col gap-2">
            <Button
              label="Sign in to join"
              onPress={() => navigate({ to: "/sign-in", search: { redirect: `/join/${code}` } })}
            />
            <Link
              to="/sign-up"
              search={{ redirect: `/join/${code}`, code }}
              className="text-aqua font-bold text-sm text-center no-underline hover:underline"
            >
              Create an account to join
            </Link>
          </div>
        )}
      </Card>
    </Screen>
  );
}
